// src/components/ActivoQrModal.jsx
// Modal con el codigo QR de un activo: al escanearlo abre su ficha de
// detalle (/activos/:codigo). Esa ruta ya esta restringida a admin/soporte
// (RequireRole en App.js) -- quien no sea staff y escanee el QR solo llega
// a la pantalla de Acceso Denegado, asi que no hace falta logica extra de
// permisos aca, el control ya existe del lado de la ruta.
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import QRCode from 'qrcode';
import { X, Download, Copy, Check } from './icons';
import { useToast } from '../contexts/ToastContext';

export default function ActivoQrModal({ activo, onClose }) {
  const { showToast } = useToast();
  const [dataUrl, setDataUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  const rawBasePath = window.APP_CONFIG?.BASE_PATH ?? '/';
  const basePath = rawBasePath === '/' ? '' : rawBasePath;
  const url = `${window.location.origin}${basePath}/activos/${encodeURIComponent(activo.codigo)}`;

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(url, { width: 480, margin: 2, errorCorrectionLevel: 'M' })
      .then((durl) => { if (!cancelled) setDataUrl(durl); })
      .catch(() => { if (!cancelled) showToast('Error', 'No se pudo generar el código QR', 'error'); });
    return () => { cancelled = true; };
  }, [url, showToast]);

  const handleDescargar = () => {
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `QR-${activo.codigo}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleCopiar = async () => {
    try {
      if (dataUrl && navigator.clipboard && window.ClipboardItem) {
        const blob = await (await fetch(dataUrl)).blob();
        await navigator.clipboard.write([new window.ClipboardItem({ [blob.type]: blob })]);
      } else {
        await navigator.clipboard.writeText(url);
      }
      setCopied(true);
      showToast('Copiado', 'Código QR copiado al portapapeles', 'success');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Fallback final: copiar solo el link si copiar la imagen falla
      try {
        await navigator.clipboard.writeText(url);
        showToast('Copiado', 'No se pudo copiar la imagen, se copió el link', 'info');
      } catch {
        showToast('Error', 'No se pudo copiar', 'error');
      }
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Código QR del activo</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-col items-center">
          <div className="w-56 h-56 flex items-center justify-center bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
            {dataUrl ? (
              <img src={dataUrl} alt={`QR de ${activo.codigo}`} className="w-full h-full p-2" />
            ) : (
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            )}
          </div>
          <p className="mt-3 font-semibold text-gray-900 dark:text-white">{activo.codigo}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-1 break-all">{url}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-2">
            Al escanearlo se abre la ficha del activo. Solo el personal de soporte y administradores pueden verla.
          </p>
        </div>

        <div className="flex gap-2 mt-6">
          <button
            onClick={handleDescargar}
            disabled={!dataUrl}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Download className="w-4 h-4" />
            Descargar
          </button>
          <button
            onClick={handleCopiar}
            disabled={!dataUrl}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-lg transition-colors"
          >
            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copiado' : 'Copiar'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
